import React, { useEffect } from "react"
import Script from "next/script"
import dynamic from "next/dynamic"
import { useSelector, useDispatch } from "react-redux"

import { COMPONENT_TYPES, BV_SCRIPT_URL } from "@/constants"
import { SCENE7_JS_URL } from "@/components/ImageGallery/v2/constants"

import {
  getUserPersona,
  filterBackEndJson,
  getQuickViewAuth,
  updateAnalyticPageUrl,
  getDataLayerObj,
} from "@/utils/helper"

import { isObjectEmpty, recursiveDataFunction } from "@/utils/"
import { processImageData } from "@/components/MultiFinshProductBundle/utils"

import { getBundlePdpData } from "@/responseProcessors/productResponseProcessor"

import { StoreProvider } from "@/components/MultiFinshProductBundle/context/store"
import { getTeaserFragments } from "@/components/ProductDetail/v3/productDetailsHelper"

import {
  selectGenericState,
  setAuthorData,
} from "@/store/features/genericSlice"
import { selectPdpState } from "@/store/features/pdpSlice"
import { selectAuthState } from "@/store/features/authSlice"

import MultiFinshProductBundle from "@/components/MultiFinshProductBundle/MultiFinshProductBundle"

import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import NextHead from "@/components/core/Head/NextHead"

import ProductExf from "@/components/ProductDetail/v3/ProductLandingTeasers/ProductExf"

const FallBackUI = dynamic(() =>
  import("@/components/Shared/ErrorBoundary/ErrorPage")
)

import usePageAuth from "@/hooks/usePageAuth"

const BundlePdpPage = props => {
  const {
    slug = "",
    pageModelData,
    authoredData,
    pdpData = {},
    header,
    footer,
    errorsData,
    fullUrl,
    config = {},
    rawAnalyticsData = "",
    seoProperties: { email = "", phoneNum = "" } = {},
  } = props || {}

  if (!isObjectEmpty(errorsData)) {
    console.error("getServerSideProps Error", errorsData)
  }

  usePageAuth()

  const quickViewAuthData = getQuickViewAuth(footer)

  const { access_token: accessToken } = useSelector(selectAuthState)
  const { pageIsInteractive } = useSelector(selectGenericState)
  const { isTeaserLoaded } = useSelector(selectPdpState)

  const dispatch = useDispatch()

  const {
    internationalization: { language = "", country = "" } = {},
    general: { priceSpiderKey = "", siteName = "" } = {},
  } = config

  const {
    globalScript = "",
    footerScript = "",
    templateName = "",
    language: metaLanguage = "",
  } = pageModelData

  const bundlePdpData = getBundlePdpData(pdpData)

  const {
    pdpDetails: {
      name: metaTitles = "",
      desc: metaDescriptions = "",
      sku = "",
      productLocalCategory = "",
    } = {},
    imageGallery: { bundleIMageURL = "", bundleListImages = [] } = {},
  } = bundlePdpData

  const scrollToTop = () => {
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    scrollToTop()
    window.needPdpdata = {
      productSubCatergory: productLocalCategory?.toLowerCase(),
      skuID: sku,
      siteName: siteName.toLowerCase(),
    }
  }, [pdpData])

  useEffect(() => {
    if (pdpData && accessToken && pageIsInteractive && !isTeaserLoaded) {
      getTeaserFragments(pageModelData, pdpData)
    }
  }, [accessToken, pdpData, pageIsInteractive, isTeaserLoaded])

  useEffect(() => {
    const filterData = filterBackEndJson(pageModelData)
    const tempData = recursiveDataFunction(
      filterData,
      COMPONENT_TYPES.PRODUCT_DETAILS
    ).at(0)

    dispatch(setAuthorData(tempData))
  }, [pageModelData])

  // To set initial datalayer page properties
  const analyticsData = updateAnalyticPageUrl(rawAnalyticsData, fullUrl)
  useEffect(() => {
    if (analyticsData) getDataLayerObj(analyticsData)
  }, [analyticsData])

  if (errorsData.pdpData) {
    return <FallBackUI />
  }

  // Set the page title,description and image, specific to bundles
  const pageTitle = `${metaTitles} | ${sku} | ${siteName.toUpperCase()}`
  const pageDescription = `Shop the ${metaTitles}, including the ${metaDescriptions}.`
  const selectedSkus = []
  for (const value of bundleListImages) {
    selectedSkus.push(value[0].sku)
  }
  const { imageURL } = processImageData(
    bundleListImages,
    selectedSkus,
    bundleIMageURL
  )

  return (
    <>
      <NextHead
        psKey={priceSpiderKey}
        psCountry={country}
        psLanguage={language}
        fullUrl={fullUrl}
        metaLanguage={metaLanguage}
        pdpData={pdpData}
        skuId={sku}
        siteName={siteName}
        isBundleProduct
        persona={getUserPersona()}
        config={config}
      />

      <Script strategy="afterInteractive" src={BV_SCRIPT_URL} async />

      <Script
        key="zoomdatapdp"
        id="zoomdatapdp"
        src={`${SCENE7_JS_URL}ZoomViewer.js`}
      />
      <Script type="text/javascript" src={`${SCENE7_JS_URL}SpinViewer.js`} />

      {!isObjectEmpty(header) ? (
        <GlobalPageHeader
          header={header}
          title={pageTitle}
          description={pageDescription}
          templateName={templateName}
          globalScripting={globalScript}
          isPdp={true}
          fullUrl={fullUrl}
          siteName={siteName}
          email={email}
          phone={phoneNum}
          imageUrl={imageURL}
        />
      ) : null}

      <StoreProvider value={bundlePdpData}>
        <MultiFinshProductBundle
          bundlePdpData={bundlePdpData}
          slug={slug}
          skuId={sku}
          quickViewAuthData={quickViewAuthData}
          authoredData={authoredData}
          config={config}
        />
      </StoreProvider>
      <ProductExf data={pageModelData} />

      {!isObjectEmpty(footer) ? (
        <GlobalPageFooter footer={footer} footerScripting={footerScript} />
      ) : null}
    </>
  )
}

export default BundlePdpPage
